import { z } from 'zod';
import { baseListQuerySchema, sortOrderSchema, SortOrder } from './helpers';
import { knownTotalQuerySchema } from './known-total';
import { ColumnDataTypes, FilterOperators, FiltersSchema, TableResponseMetaSchema, createTableQuerySchema, } from './table';
import { PLATFORM_IDS } from './platform-registry';
/**
 * MCP Transport types
 */
export const mcpTransportSchema = z.enum([
    'http',
    'stdio_local',
    'stdio_repo',
    'stdio_package',
]);
/**
 * Display labels for each MCP transport. Single source of truth shared by the
 * frontend render and the backend search builder (matches label + raw value).
 */
export const transportLabels = {
    http: 'HTTP',
    stdio_local: 'Command + Local Files',
    stdio_repo: 'Command + Repository',
    stdio_package: 'Command + Package',
};
// Org MCP status display labels — single source for the search expr + frontend options.
export const statusLabels = {
    running: 'Active',
    idle: 'Inactive',
};
/**
 * Client type - identifies the IDE/agent platform.
 * Derived from the platform registry so adding a new platform is a
 * compile-checked, single-location change.
 *
 * Used for:
 * - Kirin-Client-Type header identification
 * - MCP configuration sources
 * - Client activity tracking
 * - API key platform tracking
 */
export const ClientType = Object.fromEntries(PLATFORM_IDS.map(id => [id.charAt(0).toUpperCase() + id.slice(1), id]));
/**
 * Zod schema for ClientType validation.
 * Values are derived from the platform registry.
 */
export const clientTypeSchema = z.enum(PLATFORM_IDS);
/**
 * Client types the SERVER assigns as provenance markers — never accepted from
 * client-supplied input (KIR-3908 review). They stay in the registry/enum so the
 * `findings.client_type` column can store them and the UI can render their
 * `displayName`, but every ingest boundary rejects them so a client cannot
 * self-assert the provenance (e.g. label its own `/api/scans` as "via hook").
 * The server stamps them out-of-band (see `ClientMetadata.clientTypeOverride`).
 */
export const SERVER_ASSIGNED_CLIENT_TYPES = new Set([
    'claude_desktop_hook',
]);
/**
 * Coerce an arbitrary, client-supplied value (e.g. a `Kirin-Client-Type`
 * header or `extraData.client.name` from a scan body) into a canonical
 * `PlatformId`, or `null` when it is not one.
 *
 * This is the shared ingest-boundary platform gate expressed as a contract
 * transform: data/handler layers parse untrusted input through this schema
 * instead of re-implementing validation, and the frontend can rely on the
 * exact same rule. Never throws — unrecognized OR server-assigned-only input
 * resolves to `null`.
 */
export const platformIdInputSchema = z
    .unknown()
    .transform((value) => {
    const result = clientTypeSchema.safeParse(value);
    if (!result.success)
        return null;
    return SERVER_ASSIGNED_CLIENT_TYPES.has(result.data) ? null : result.data;
});
/**
 * @deprecated Use clientTypeSchema instead
 */
export const mcpSourceSchema = clientTypeSchema;
/**
 * MCP Type (local vs remote origin)
 * - local: Server runs from local files (stdio_local or stdio_repo)
 * - remote: Server accessed via network (http) or from package registry (stdio_package)
 */
export const mcpTypeSchema = z.enum(['local', 'remote']);
/**
 * Compact tool descriptor surfaced on an MCP inventory entry.
 * Mirrors MCPServerToolSummary on the agent and DB side.
 */
export const mcpServerToolSummarySchema = z.object({
    name: z.string(),
    description: z.string().optional(),
});
/**
 * MCP Server list item - returned from list endpoint
 */
export const mcpServerListItemSchema = z.object({
    // Row identity: the .mcp.json configuration key (KIR-2383).
    name: z.string(),
    // Handshake-reported server name (modal across the group); null if unknown.
    serverName: z.string().nullable(),
    versions: z.array(z.string().nullable()), // All unique server versions (sorted descending)
    publisher: z.string().nullable(),
    type: mcpTypeSchema, // Local or Remote (derived from transport)
    transport: mcpTransportSchema,
    sources: z.array(mcpSourceSchema), // All unique sources (cursor, claude, etc.)
    userCount: z.number().int().min(0),
    runningCount: z.number().int().min(0), // How many instances are currently running
    firstSeenAt: z.string().datetime(),
    lastSeenAt: z.string().datetime(), // Most recent scan report for any row in this group
    url: z.string().nullable(), // Endpoint URL for HTTP transports (null when unknown)
    tools: z.array(mcpServerToolSummarySchema).nullable(), // Compact tool list (null when unknown)
    // NOTE: machineCount intentionally omitted - not actionable for users
});
/**
 * MCP Server list response with pagination
 */
export const mcpServerListResponseSchema = z.object({
    servers: z.array(mcpServerListItemSchema),
    pagination: z.object({
        page: z.number().int().min(1),
        limit: z.number().int().min(1).max(100),
        total: z.number().int().min(0),
        hasMore: z.boolean(),
    }),
});
/**
 * MCP Server user - for drill-down endpoint
 */
export const mcpServerUserSchema = z.object({
    userId: z.string().uuid(),
    email: z.string().nullable(),
    name: z.string().nullable(),
    sources: z.array(mcpSourceSchema),
    /**
     * Distinct non-null handshake names reported for this row's `config_name`;
     * length >1 signals divergent self-reports under one key (KIR-2383).
     */
    serverNames: z.array(z.string()),
    isRunning: z.boolean(),
    lastSeenAt: z.string().datetime(),
    // NOTE: machineCount intentionally omitted - not actionable for users
});
/**
 * MCP Server users response
 */
export const mcpServerUsersResponseSchema = z.object({
    serverName: z.string(),
    users: z.array(mcpServerUserSchema),
});
/** MCP row identifier — the `.mcp.json` `config_name` (KIR-2383). */
export const mcpServerNameSchema = z.string().min(1);
/**
 * Drilldown request body. Pass the row identity as `configName`. The legacy
 * `serverName` field is accepted for IDE-extension back-compat and will be
 * removed in a future release — when both are present, `configName` wins.
 *
 * The wire-level confusion this resolves: response-side
 * `mcpServerListItemSchema.serverName` carries the handshake name, while
 * request-side `serverName` historically carried the `config_name`. Use
 * `configName` for new code (KIR-2383).
 */
export const mcpServerUsersRequestBodySchema = z
    .object({
    /** Preferred row identifier — the `.mcp.json` `config_name`. */
    configName: mcpServerNameSchema.optional(),
    /** @deprecated Use `configName`. Kept for IDE-extension back-compat. */
    serverName: mcpServerNameSchema.optional(),
})
    .refine(body => Boolean(body.configName ?? body.serverName), {
    message: '`configName` is required (legacy `serverName` also accepted)',
    path: ['configName'],
});
/** Extract the row identity from a drilldown request body, preferring `configName`. */
export function getMcpConfigNameFromBody(body) {
    return (body.configName ?? body.serverName);
}
/**
 * Sort field options for MCP server list
 */
export const mcpSortFieldSchema = z.enum([
    'name',
    'version',
    'publisher',
    'type',
    'transport',
    'sources',
    'userCount',
    'runningCount', // Maps to "Status" column
    'tools', // Sorts by reported tool count
    'firstSeenAt',
    'lastSeenAt',
]);
/**
 * Sort order - re-exported from helpers for convenience.
 * Use the generic sortOrderSchema for new code.
 */
export const mcpSortOrderSchema = sortOrderSchema;
/**
 * Query schema for listing MCP servers.
 * Extends the base list query with MCP-specific sort and filter fields.
 */
export const mcpListQuerySchema = baseListQuerySchema.extend({
    sortBy: mcpSortFieldSchema.default('userCount'),
    sortOrder: sortOrderSchema.default(SortOrder.Desc),
    transport: mcpTransportSchema.optional(),
});
/**
 * Query parameters for listing MCP servers.
 * Optional fields for API client usage (no defaults).
 * Uses z.coerce for page/limit since query strings are always strings.
 */
export const listMcpServersParamsSchema = z.object({
    page: z.coerce.number().int().min(1).optional(),
    limit: z.coerce.number().int().min(1).max(100).optional(),
    search: z.string().optional(),
    transport: mcpTransportSchema.optional(),
    sortBy: mcpSortFieldSchema.optional(),
    sortOrder: mcpSortOrderSchema.optional(),
    filters: FiltersSchema.optional(),
    knownTotal: knownTotalQuerySchema,
});
/**
 * Query parameters for listing personal MCP servers (legacy endpoint).
 *
 * Extends listMcpServersParamsSchema with explicit defaults for personal context:
 * - sortBy defaults to 'name' (userCount is meaningless for personal MCPs since it's always 1)
 * - sortOrder defaults to 'asc' for alphabetical ordering
 *
 * These defaults align with the personal MCP dashboard UI and provide
 * predictable behavior for API consumers.
 */
export const personalMcpListParamsSchema = listMcpServersParamsSchema.extend({
    sortBy: mcpSortFieldSchema.default('name'),
    sortOrder: mcpSortOrderSchema.default(SortOrder.Asc),
});
// ============================================================================
// DataTable Integration: Filterable Fields & Filter Options
// ============================================================================
export const MCP_FILTERABLE_FIELDS = {
    NAME: 'name',
    VERSION: 'version',
    PUBLISHER: 'publisher',
    TYPE: 'type',
    TRANSPORT: 'transport',
    USERS: 'users',
    TOOLS: 'tools',
    STATUS: 'status',
    FIRST_SEEN_AT: 'firstSeenAt',
    LAST_SEEN_AT: 'lastSeenAt',
};
/**
 * Per-column operator allow-list for org MCP filters. Narrows the DATE
 * data-type defaults so the First Seen column rejects the `equals`/`notEquals`
 * ("Is") operators — only range operators plus the value-less operators are valid.
 */
export const MCP_OPERATOR_OVERRIDES = {
    [MCP_FILTERABLE_FIELDS.FIRST_SEEN_AT]: [
        FilterOperators.GREATER_THAN,
        FilterOperators.LESS_THAN,
        FilterOperators.BETWEEN,
        FilterOperators.IS_EMPTY,
        FilterOperators.IS_NOT_EMPTY,
    ],
};
/**
 * Fields that have a backend filter-options endpoint (dynamic options).
 * Type, transport, and status have static options in the frontend.
 * firstSeenAt is a date field with no options dropdown.
 * tools serves distinct tool names extracted from each group's latest report.
 */
export const mcpFilterOptionFieldSchema = z.enum([
    MCP_FILTERABLE_FIELDS.NAME,
    MCP_FILTERABLE_FIELDS.VERSION,
    MCP_FILTERABLE_FIELDS.PUBLISHER,
    MCP_FILTERABLE_FIELDS.USERS,
    MCP_FILTERABLE_FIELDS.TOOLS,
]);
export const mcpFilterOptionsQuerySchema = z.object({
    field: mcpFilterOptionFieldSchema,
    search: z.string().optional(),
    limit: z.coerce.number().int().positive().max(100).optional(),
    offset: z.coerce.number().int().min(0).optional(),
});
/**
 * Rich filter option item with value, label, and optional metadata.
 * Used for complex filter options like users where ID alone isn't displayable.
 */
export const filterOptionItemSchema = z.object({
    value: z.string(),
    label: z.string().optional(),
    metadata: z.record(z.string(), z.unknown()).optional(),
});
export const mcpFilterOptionsResponseSchema = z.object({
    field: mcpFilterOptionFieldSchema,
    options: z.union([z.array(z.string()), z.array(filterOptionItemSchema)]),
    pagination: z.object({
        total: z.number().int().min(0),
        limit: z.number().int().min(0),
        offset: z.number().int().min(0),
        hasMore: z.boolean(),
        nextOffset: z.number().int().min(0).nullable().optional(),
    }),
});
const MCP_FILTER_COLUMN_DATA_TYPES = {
    [MCP_FILTERABLE_FIELDS.NAME]: ColumnDataTypes.STRING,
    [MCP_FILTERABLE_FIELDS.VERSION]: ColumnDataTypes.STRING,
    [MCP_FILTERABLE_FIELDS.PUBLISHER]: ColumnDataTypes.STRING,
    [MCP_FILTERABLE_FIELDS.TYPE]: ColumnDataTypes.ENUM,
    [MCP_FILTERABLE_FIELDS.TRANSPORT]: ColumnDataTypes.ENUM,
    [MCP_FILTERABLE_FIELDS.USERS]: ColumnDataTypes.ENUM,
    [MCP_FILTERABLE_FIELDS.TOOLS]: ColumnDataTypes.ENUM,
    [MCP_FILTERABLE_FIELDS.STATUS]: ColumnDataTypes.ENUM,
    [MCP_FILTERABLE_FIELDS.FIRST_SEEN_AT]: ColumnDataTypes.DATE,
    [MCP_FILTERABLE_FIELDS.LAST_SEEN_AT]: ColumnDataTypes.DATE,
};
/**
 * Table query schema for the MCP list endpoint.
 * Handles bracket-notation filter params (e.g. name[includes]=foo,bar).
 * Used by the backend route handler.
 */
const mcpTableQueryBaseSchema = baseListQuerySchema.extend({
    sortBy: mcpSortFieldSchema.optional(),
    sortOrder: sortOrderSchema.optional(),
    filters: FiltersSchema.optional(),
    knownTotal: knownTotalQuerySchema,
});
export const mcpTableQuerySchema = createTableQuerySchema(mcpTableQueryBaseSchema, MCP_FILTER_COLUMN_DATA_TYPES, MCP_OPERATOR_OVERRIDES);
/**
 * Table response schema for MCP server list (DataTable format).
 */
export const mcpTableResponseSchema = z.object({
    data: z.array(mcpServerListItemSchema),
    pagination: z.object({
        page: z.number().int().min(1),
        limit: z.number().int().min(1),
        total: z.number().int().min(0),
        hasMore: z.boolean(),
    }),
    meta: TableResponseMetaSchema,
});
//# sourceMappingURL=mcp.js.map